import { AudioStreamMetadata } from './types';

const MAGIC = 0xCAFE;
const VERSION = 0x01;

enum PacketType {
  Audio = 0x00,
  Control = 0x01,
  Metadata = 0x02,
  Heartbeat = 0x03,
  Ack = 0x04,
  Error = 0x05,
}

interface PacketHeader {
  magic: number;
  version: number;
  type: PacketType;
  sequence: number;
  length: number;
}

export class StreamingClient {
  private ws: WebSocket | null = null;
  private serverUrl: string;
  private sequence: number = 0;
  private streamId: string | null = null;
  private onStreamId: ((streamId: string) => void) | null = null;

  constructor(serverUrl: string = 'ws://localhost:5122/api/stream/push') {
    this.serverUrl = serverUrl;
  }

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(this.serverUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        resolve();
      };

      this.ws.onerror = (error) => {
        const errorMessage = `WebSocket connection error: ${error.type || 'Unknown error'}`;
        console.error(errorMessage, error);
        reject(new Error(errorMessage));
      };

      this.ws.onmessage = (event) => {
        if (event.data instanceof Blob) {
          event.data.arrayBuffer().then((buffer) => {
            this.handleMessage(new Uint8Array(buffer));
          });
        } else if (event.data instanceof ArrayBuffer) {
          this.handleMessage(new Uint8Array(event.data));
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket closed');
      };
    });
  }

  private handleMessage(data: Uint8Array): void {
    if (data.length < 8) return;

    const header = this.parseHeader(data);
    if (header.magic !== MAGIC) return;

    const payload = data.slice(8, 8 + header.length);

    if (header.type === PacketType.Metadata) {
      // Stream ID response
      const json = new TextDecoder().decode(payload);
      const response = JSON.parse(json);
      if (response.streamId) {
        this.streamId = response.streamId;
        if (this.onStreamId) {
          this.onStreamId(response.streamId);
        }
      }
    } else if (header.type === PacketType.Ack) {
      // Acknowledgment
      console.log('Received ACK, sequence:', header.sequence);
    }
  }

  private parseHeader(data: Uint8Array): PacketHeader {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return {
      magic: view.getUint16(0, true),
      version: data[2],
      type: data[3],
      sequence: view.getUint16(4, true),
      length: view.getUint16(6, true),
    };
  }

  sendStreamStart(metadata: AudioStreamMetadata): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    const metadataJson = JSON.stringify({
      sampleRate: metadata.sampleRate || 16000,
      channels: metadata.channels || 1,
      bitDepth: metadata.bitDepth || 16,
      codec: metadata.codec || 'opus',
      bitrate: metadata.bitrate || 48000,
      callInfo: metadata.callInfo || null,
    });

    const payload = new TextEncoder().encode(metadataJson);
    const packet = this.createPacket(PacketType.Metadata, payload);
    this.ws.send(packet);
  }

  sendAudioData(audioData: Uint8Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const packet = this.createPacket(PacketType.Audio, audioData);
    this.ws.send(packet);
  }

  sendStreamEnd(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const controlJson = JSON.stringify({
      type: 3, // StreamEnd
      reason: 'normal',
    });

    const payload = new TextEncoder().encode(controlJson);
    const packet = this.createPacket(PacketType.Control, payload);
    this.ws.send(packet);
  }

  private createPacket(type: PacketType, payload: Uint8Array): ArrayBuffer {
    const headerSize = 8;
    const packet = new ArrayBuffer(headerSize + payload.length);
    const view = new DataView(packet);
    const dataView = new Uint8Array(packet);

    // Header
    view.setUint16(0, MAGIC, true);
    dataView[2] = VERSION;
    dataView[3] = type;
    view.setUint16(4, this.sequence++, true);
    view.setUint16(6, payload.length, true);

    // Payload
    dataView.set(payload, headerSize);

    return packet;
  }

  setOnStreamId(callback: (streamId: string) => void): void {
    this.onStreamId = callback;
  }

  endCurrentStream(): void {
    // End current stream but keep connection alive
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendStreamEnd();
      this.streamId = null;
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.sendStreamEnd();
      this.ws.close();
      this.ws = null;
    }
    this.streamId = null;
  }

  getStreamId(): string | null {
    return this.streamId;
  }
}

